<ng-container *transloco="let t; prefix: 'workOrder'">
<!-- KPI Widgets -->
<div class="kpi-widgets-container">
  <div class="row gx-4">
    <div class="col-lg-3 col-md-6">
      <app-total-work-orders-widget></app-total-work-orders-widget>
    </div>
    <div class="col-lg-3 col-md-6">
      <app-overdue-widget></app-overdue-widget>
    </div>
    <div class="col-lg-3 col-md-6">
      <app-in-progress-widget></app-in-progress-widget>
    </div>
    <div class="col-lg-3 col-md-6">
      <app-avg-resolution-widget></app-avg-resolution-widget>
    </div>
  </div>
</div>

<!-- Search & Filters -->
<div
  class="filter-container pb-3 d-flex align-items-center justify-content-between">
  <app-search
    [placeholder]="t('filters.searchPlaceholder')"
    [disabled]="isLoading"
    (searchTermUpdated)="onSearchTermUpdate($event)"
    [searchTerm]="searchTerm"
    [customWidth]="'380px'">
  </app-search>

  <div class="d-flex align-items-center gap-2">
    <app-make-model-filter
      [filter]="makeModelFilter"
      [accountFilterChanged$]="accountIdsChanged$"
      (filterChanged)="onMakeAndModelChange($event)">
    </app-make-model-filter>

    <app-workshop-filter
      [filter]="workshopFilter"
      [accountFilterChanged$]="accountIdsChanged$"
      (filterChanged)="onWorkshopFilterChange($event)">
    </app-workshop-filter>

    <div ngbDropdown class="status-filter-dropdown">
      <button class="filter-button" type="button" ngbDropdownToggle>
        <span>{{ WorkOrderStatusLabels[statusFilter] | transloco }}</span>
        <i class="ph ph-caret-down"></i>
      </button>
      <div ngbDropdownMenu class="dropdown-menu">
        <button
          ngbDropdownItem
          (click)="onStatusFilterChange(WorkOrderStatus.All)">
          {{ t('status.all') }}
        </button>
        <button
          ngbDropdownItem
          (click)="onStatusFilterChange(WorkOrderStatus.Open)">
          {{ t('status.open') }}
        </button>
        <button
          ngbDropdownItem
          (click)="onStatusFilterChange(WorkOrderStatus.InProgress)">
          {{ t('status.inProgress') }}
        </button>
        <button
          ngbDropdownItem
          (click)="onStatusFilterChange(WorkOrderStatus.Completed)">
          {{ t('status.completed') }}
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Work Orders Table -->
<div class="main-table work-orders-overview" [class.no-internal-scroll]="isLoading || hasError || workOrders.length === 0">
  <table class="table">
    <thead>
      <tr>
        <th class="work-order-id">
          {{ t('table.workOrderId') }}
          <button
            class="filter-button"
            [class.active]="sortColumn === 'workOrderId'"
            (click)="onSort('workOrderId')">
            <i class="ph ph-arrows-down-up"></i>
          </button>
        </th>
        <th class="asset">
          {{ t('table.asset') }}
          <button
            class="filter-button"
            [class.active]="sortColumn === 'asset'"
            (click)="onSort('asset')">
            <i class="ph ph-arrows-down-up"></i>
          </button>
        </th>
        <th class="source">{{ t('table.source') }}</th>
        <th class="work-type">{{ t('table.serviceType') }}</th>
        <th class="reason">{{ t('table.reason') }}</th>
        <th class="estimated-cost">
          {{ t('table.estCostAndTime') }}
          <button
            class="filter-button"
            [class.active]="sortColumn === 'estimatedCost'"
            (click)="onSort('estimatedCost')">
            <i class="ph ph-arrows-down-up"></i>
          </button>
        </th>
        <th class="priority">
          {{ t('table.priority') }}
          <button
            class="filter-button"
            [class.active]="sortColumn === 'priority'"
            (click)="onSort('priority')">
            <i class="ph ph-arrows-down-up"></i>
          </button>
        </th>
        <th class="scheduled-date">
          {{ t('table.scheduledDate') }}
          <button
            class="filter-button"
            [class.active]="sortColumn === 'scheduledDate'"
            (click)="onSort('scheduledDate')">
            <i class="ph ph-arrows-down-up"></i>
          </button>
        </th>
        <th class="status">
          {{ t('table.status') }}
          <button
            class="filter-button"
            [class.active]="sortColumn === 'status'"
            (click)="onSort('status')">
            <i class="ph ph-arrows-down-up"></i>
          </button>
        </th>
        <th class="actions-small">{{ t('table.actions') }}</th>
      </tr>
    </thead>

    @if (isLoading) {
      <tbody>
        <tr class="loading-row">
          <td colspan="10">
            <div class="spinner-container">
              <div class="spinner-border text-danger" role="status"></div>
            </div>
          </td>
        </tr>
      </tbody>
    } @else if (hasError) {
      <tbody>
        <tr class="error-row">
          <td colspan="10">
            <div class="error-container">
              <app-error-state></app-error-state>
            </div>
          </td>
        </tr>
      </tbody>
    } @else {
      <tbody>
        @if (workOrders.length === 0) {
          <tr class="empty-row">
            <td colspan="10">
              <div class="table__no-data">
                <app-empty-state></app-empty-state>
              </div>
            </td>
          </tr>
        }

        @for (workOrder of workOrders; track workOrder.workOrderId) {
          <tr>
            <td class="work-order-id">
              <p>{{ workOrder.workOrderNumber }}</p>
            </td>
            <td class="asset">
              <div
                class="asset-name-wrapper"
                [ngbTooltip]="workOrder.assetName"
                placement="top"
                container="body">
                {{ workOrder.assetName }}
              </div>
            </td>
            <td class="source">
              <div class="source-cell">
              <p class="source-label" [ngbTooltip]="WorkOrderSourceLabels[workOrder.source] | transloco">{{ WorkOrderSourceLabels[workOrder.source] | transloco }}</p>
              @if (workOrder.faultImages && workOrder.faultImages.length > 0) {
                <button
                  type="button"
                  class="photos-capsule"
                  [ngbTooltip]="getPhotoCountLabel(workOrder)"
                  [attr.aria-label]="getPhotoCountLabel(workOrder)"
                  (click)="onViewPhotos(workOrder); $event.stopPropagation()">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M7.99967 5.3335C6.55434 5.3335 5.33301 6.55483 5.33301 8.00016C5.33301 9.4455 6.55434 10.6668 7.99967 10.6668C9.44501 10.6668 10.6663 9.4455 10.6663 8.00016C10.6663 6.55483 9.44501 5.3335 7.99967 5.3335ZM7.99967 9.3335C7.28967 9.3335 6.66634 8.71016 6.66634 8.00016C6.66634 7.29016 7.28967 6.66683 7.99967 6.66683C8.70967 6.66683 9.33301 7.29016 9.33301 8.00016C9.33301 8.71016 8.70967 9.3335 7.99967 9.3335Z" fill="white"/>
                    <path d="M13.333 3.3335H11.609L9.80434 1.52883C9.67967 1.4035 9.51034 1.3335 9.33301 1.3335H6.66634C6.48901 1.3335 6.31967 1.4035 6.19501 1.52883L4.39034 3.3335H2.66634C1.93101 3.3335 1.33301 3.9315 1.33301 4.66683V12.0002C1.33301 12.7355 1.93101 13.3335 2.66634 13.3335H13.333C14.0683 13.3335 14.6663 12.7355 14.6663 12.0002V4.66683C14.6663 3.9315 14.0683 3.3335 13.333 3.3335ZM2.66634 12.0002V4.66683H4.66634C4.84367 4.66683 5.01301 4.59683 5.13767 4.4715L6.94234 2.66683H9.05701L10.8617 4.4715C10.9863 4.59683 11.1557 4.66683 11.333 4.66683H13.333L13.3343 12.0002H2.66634Z" fill="white"/>
                  </svg>
                  <span>{{ workOrder.faultImages.length }}</span>
                </button>
              }
              </div>
            </td>
            <td class="work-type">
              <div class="work-type-cell">
                <p class="work-type-name">
                  {{ WorkOrderServiceTypeLabels[workOrder.serviceType] | transloco }}
                </p>
                <p class="workshop-name">{{ workOrder.workshopName }}</p>
              </div>
            </td>
            <td class="reason">
              <div
                class="asset-name-wrapper"
                [ngbTooltip]="workOrder.localizedReason || workOrder.reason || '-'"
                placement="top"
                container="body">
                {{ workOrder.localizedReason || workOrder.reason || '-' }}
              </div>
            </td>
            <td class="estimated-cost">
              <div class="cost-time-cell">
                @if (
                  workOrder.estimatedCost === 0 && !workOrder.estimatedTime
                ) {
                  <p class="cost">-</p>
                } @else {
                  <p class="cost">
                    {{
                      workOrder.estimatedCost
                        ? formatCost(workOrder.estimatedCost)
                        : '-'
                    }}
                  </p>
                  @if (workOrder.estimatedTime) {
                    <p class="time">
                      <span class="time-value">{{
                        workOrder.estimatedTime
                      }}</span
                      ><span class="time-suffix">{{ t('table.hrs') }}</span>
                    </p>
                  }
                }
              </div>
            </td>
            <td class="priority">
              <span
                class="priority-badge"
                [ngClass]="getPriorityClass(workOrder.priority)">
                <span class="priority-indicator">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M6.63533 0C2.97667 0 0 2.99067 0 6.66667C0 10.3427 2.99067 13.3333 6.66667 13.3333C10.3427 13.3333 13.3333 10.3427 13.3333 6.66667C13.3333 2.99067 10.3287 0 6.63533 0ZM7.33333 10H6V8.66667H7.33333V10ZM7.33333 7.33333H6V3.33333H7.33333V7.33333Z" />
                  </svg>
                </span>
                {{ WorkOrderPriorityLabels[workOrder.priority] | transloco }}
              </span>
            </td>
            <td class="scheduled-date">
              <p>
                {{
                  workOrder.scheduledDate
                    ? formatDate(workOrder.scheduledDate)
                    : '-'
                }}
              </p>
            </td>
            <td class="status">
              <span
                class="status-badge"
                [ngClass]="
                  getWorkOrderStatusClass(getDisplayStatus(workOrder))
                ">
                <span class="status-indicator">
                  @if (
                    getDisplayStatus(workOrder) === WorkOrderStatus.Open ||
                    getDisplayStatus(workOrder) === WorkOrderStatus.InProgress
                  ) {
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M6.66667 0C2.99067 0 0 2.99067 0 6.66667C0 10.3427 2.99067 13.3333 6.66667 13.3333C10.3427 13.3333 13.3333 10.3427 13.3333 6.66667C13.3333 2.99067 10.3427 0 6.66667 0ZM8.862 9.80467L6 6.94267V2.66667H7.33333V6.39067L9.80467 8.862L8.862 9.80467Z" />
                    </svg>
                  }
                  @if (
                    getDisplayStatus(workOrder) === WorkOrderStatus.Completed
                  ) {
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M6.66667 0C2.99067 0 0 2.99067 0 6.66667C0 10.3427 2.99067 13.3333 6.66667 13.3333C10.3427 13.3333 13.3333 10.3427 13.3333 6.66667C13.3333 2.99067 10.3427 0 6.66667 0ZM5.334 9.60867L2.85867 7.13867L3.8 6.19467L5.33267 7.72467L8.862 4.19533L9.80467 5.138L5.334 9.60867Z" />
                    </svg>
                  }
                  @if (
                    getDisplayStatus(workOrder) === WorkOrderStatus.Pending ||
                    getDisplayStatus(workOrder) === WorkOrderStatus.Rejected ||
                    getDisplayStatus(workOrder) === OVERDUE_STATUS
                  ) {
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M6.63533 0C2.97667 0 0 2.99067 0 6.66667C0 10.3427 2.99067 13.3333 6.66667 13.3333C10.3427 13.3333 13.3333 10.3427 13.3333 6.66667C13.3333 2.99067 10.3287 0 6.63533 0ZM7.33333 10H6V8.66667H7.33333V10ZM7.33333 7.33333H6V3.33333H7.33333V7.33333Z" />
                    </svg>
                  }
                </span>
                {{ getDisplayStatusLabel(getDisplayStatus(workOrder)) | transloco }}
              </span>
            </td>
            <td class="actions">
              <div ngbDropdown class="action-dropdown" placement="auto">
                <button
                  class="btn btn-link action-btn"
                  ngbDropdownToggle
                  [attr.aria-label]="t('table.actionsFor', { workOrderId: workOrder.workOrderId })">
                  <i class="ph ph-dots-three-vertical"></i>
                </button>
                <div ngbDropdownMenu class="dropdown-menu">
                  <button
                    ngbDropdownItem
                    (click)="onEdit(workOrder)"
                    [disabled]="
                      workOrder.status === WorkOrderStatus.Completed ||
                      workOrder.status === WorkOrderStatus.InProgress
                    ">
                    {{ t('rowActions.edit') }}
                  </button>
                  <button
                    ngbDropdownItem
                    (click)="onScheduleAssign(workOrder)"
                    [disabled]="
                      workOrder.status === WorkOrderStatus.Completed ||
                      workOrder.status === WorkOrderStatus.InProgress
                    ">
                    {{ t('rowActions.scheduleAndAssign') }}
                  </button>
                  <button ngbDropdownItem (click)="onViewDetails(workOrder)">
                    {{ t('rowActions.viewDetails') }}
                  </button>
                  <button
                    ngbDropdownItem
                    class="text-danger"
                    (click)="onDelete(workOrder)">
                    {{ t('rowActions.delete') }}
                  </button>
                </div>
              </div>
            </td>
          </tr>
        }
      </tbody>
    }
  </table>
</div>

<!-- Footer -->
<div class="bottom-options d-flex align-items-center justify-content-between">
  <p class="showing-text">{{ 'common.showingOutOf' | transloco: { current: showingCount, total: totalCount } }}</p>

  <app-pagination
    [totalPages]="totalPages"
    [currentPage]="page"
    (pageChange)="onPageChange($event)">
  </app-pagination>

  <div
    ngbDropdown
    #exportDropdown="ngbDropdown"
    class="export-listing"
    [class.invisible]="true"
    [autoClose]="true"
    placement="top-end">
    <div class="common-drop-down dropdown">
      <button
        class="btn btn-secondary dropdown-toggle d-flex align-items-center gap-2 export-btn"
        type="button"
        ngbDropdownToggle
        [disabled]="isLoading || isExporting">
        <span>{{ isExporting ? t('export.exporting') : t('export.export') }}</span>
        @if (!isExporting) {
          <img src="./assets/images-fuel/downloadIcon.svg" [attr.alt]="'common.export' | transloco" />
        }
      </button>

      <ul ngbDropdownMenu class="dropdown-menu export-menu">
        <li>
          <a
            ngbDropdownItem
            class="dropdown-item"
            (click)="exportDropdown.close(); onExport('excel')"
            >{{ t('export.excel') }}</a
          >
        </li>
        <li>
          <a
            ngbDropdownItem
            class="dropdown-item"
            (click)="exportDropdown.close(); onExport('csv')"
            >{{ t('export.csv') }}</a
          >
        </li>
      </ul>
    </div>
  </div>
</div>
</ng-container>